/**
 * Product Modal — Liquid-rendered via Section Rendering API
 * Replaces the product modal portion of custom-productCart-drawer.js
 *
 * Fetches product modal HTML from Shopify, handles variant selection
 * and calculator, delegates add-to-cart to StikCartDrawer.
 */

const ProductModal = {
  selectors: {
    modal: '#product-modal-wrapper',
    content: '#product-modal-slot',
    overlay: '#product-modal-overlay',
  },

  sectionId: 'product-modal',
  variants: [],
  selectedOptions: [],
  editMode: null, // { lineKey, variantId, quantity } when editing from cart
  opener: null,
  variantOpener: null,
  affirmObserver: null,
  initialized: false,

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.bindEvents();
  },

  bindEvents() {
    document.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]');
      if (!action) return;

      switch (action.dataset.action) {
        case 'open-product-modal':
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.open(action.dataset.productHandle, null, action);
          break;
        case 'open-product-calculator':
          e.preventDefault();
          this.open(action.dataset.productHandle, null, action).then(() => {
            const toggle = document.querySelector('#product-modal-slot [data-action="toggle-calculator"]');
            if (toggle) window.requestAnimationFrame(() => this.toggleCalculator(toggle));
          });
          break;
        case 'close-product-modal':
          e.preventDefault();
          this.close();
          break;
        case 'close-variant-drawer':
          e.preventDefault();
          this.closeVariantDrawer();
          break;
        case 'modal-add-to-cart':
          e.preventDefault();
          this.addToCart();
          break;
        case 'modal-update-cart':
          e.preventDefault();
          this.updateCartItem();
          break;
        case 'toggle-calculator':
          e.preventDefault();
          this.toggleCalculator(action);
          break;
      }
    });

    // Option row click → open variant sub-drawer
    document.addEventListener('click', (e) => {
      var row = e.target.closest('.product-modal__option-row');
      if (row && !e.target.closest('.product-modal__option-values') && !e.target.closest('.product-modal__option-btn')) {
          this.openVariantDrawer(row);
        return;
      }
      // Option value selection (from within variant drawer)
      var btn = e.target.closest('.variant-option-choice');
      if (btn) {
        var index = parseInt(btn.dataset.optionIndex);
        var value = btn.dataset.optionValue;
        // Update selected options
        this.selectedOptions[index] = value;
        // Update the display text on the main option row
        var rows = document.querySelectorAll('.product-modal__option-row');
          if (rows[index]) {
            var selectedText = rows[index].querySelector('.product-modal__option-selected');
            if (selectedText) selectedText.textContent = value;
            rows[index].setAttribute('aria-label', (rows[index].dataset.optionName || 'Option') + '. Currently selected option is ' + value + '. Change option');
          var selectedSource = rows[index].querySelector('.product-modal__option-btn[data-option-value="' + CSS.escape(value) + '"]');
          var selectedImage = selectedSource ? selectedSource.querySelector('.product-modal__option-meta-image') : null;
          var rowIcon = rows[index].querySelector('.atc-dr-product-variant-icon');
          if (rowIcon) {
            rowIcon.replaceChildren();
            if (selectedImage) {
              var rowImage = selectedImage.cloneNode(true);
              rowImage.removeAttribute('class');
              rowImage.width = 40;
              rowImage.height = 40;
              rowIcon.appendChild(rowImage);
            }
          }
          // Update selected state on buttons
          rows[index].querySelectorAll('.product-modal__option-btn').forEach(function(b) {
            b.classList.toggle('is-selected', b.dataset.optionValue === value);
            b.setAttribute('aria-pressed', b.dataset.optionValue === value ? 'true' : 'false');
          });
        }
        // Find matching variant and update UI
        var variant = this.findVariant();
        if (variant) this.updateVariantUI(variant);
        // Close variant drawer
        this.closeVariantDrawer();
      }
    });

    // Quantity change → update total
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-qty-select]') && e.target.closest('.product-modal')) {
        this.updateTotal();
        this.updateCartActionLabel();
      }
    });

    // Close on overlay
    document.addEventListener('click', (e) => {
      if (e.target.id === 'product-modal-overlay') this.close();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (document.getElementById('variant_modal')?.classList.contains('visible')) {
          e.preventDefault();
          this.closeVariantDrawer();
        } else {
          this.close();
        }
        return;
      }
      if (e.key === 'Tab' && this.isOpen()) {
        this.trapFocus(e);
      }
      if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('.product-modal__option-row')) {
        e.preventDefault();
        this.openVariantDrawer(e.target.closest('.product-modal__option-row'));
      }
    }, true);
  },

  isOpen() {
    var menuCtx = document.getElementById('js-default-menu');
    return menuCtx ? menuCtx.classList.contains('js-menu__expanded') : false;
  },

  /**
   * Fetch product modal HTML via Section Rendering API and open
   */
  async open(handle, editOptions, opener) {
    if (!handle) return;
    this.editMode = editOptions || null;
    this.opener = opener || document.activeElement;

    // Open the cart drawer and load product UI into it
    const menuCtx = document.getElementById("js-default-menu");
    const mainNav = document.getElementById('main-nav');
    if (menuCtx && mainNav) {
      document.getElementById('product-modal-slot').innerHTML = '<div style="padding:40px;text-align:center;">Loading...</div>';
      // Use app.js native mechanism: add active + open-cc
      menuCtx.classList.add('js-menu__expanded');
      mainNav.classList.add('js-menu__expanded');
      mainNav.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    try {
      const response = await fetch(
        `/products/${handle}?sections=${this.sectionId}`
      );
      if (!response.ok) throw new Error('Failed to fetch product');

      const data = await response.json();
      const html = data[this.sectionId];

      // Render product UI inside the product drawer slot
      var slot = document.getElementById('product-modal-slot');
      if (slot) {
        slot.innerHTML = html;
      }
      var modalContent = slot ? slot.querySelector('.product-modal') : null;
      var title = modalContent ? modalContent.dataset.productTitle : '';
      if (mainNav && title) {
        mainNav.setAttribute('aria-label', 'Product options for ' + title);
      }

      // Parse variant data from embedded JSON
      var variantScript = slot ? slot.querySelector('[data-product-variants]') : null;
      if (variantScript) {
        try {
          this.variants = JSON.parse(variantScript.textContent);
        } catch (e) {
          this.variants = [];
        }
      }

      // Build selected options from initially selected buttons
      this.selectedOptions = [];
      if (slot) {
        slot.querySelectorAll('.product-modal__option-btn.is-selected').forEach(function(btn) {
          this.selectedOptions[parseInt(btn.dataset.optionIndex)] = btn.dataset.optionValue;
        }.bind(this));
      }

      // Initialize calculator if present
      var calcContainer = slot ? slot.querySelector('.product-modal__calculator') : null;
      if (calcContainer && typeof SqFtCalculator !== 'undefined') {
        SqFtCalculator.init(calcContainer);
      }

      // Refresh Affirm widget for the new content
      this.observeAffirmWidget(slot);
      if (typeof affirm !== 'undefined' && affirm.ui) {
        try { affirm.ui.refresh(); } catch (e) {}
      }

      // Edit mode: pre-select variant, quantity, and change button to "Update Cart"
      if (this.editMode && slot) {
        var editVariant = this.variants.find(function(v) {
          return String(v.id) === String(this.editMode.variantId);
        }.bind(this));

        if (editVariant) {
          // Select the correct options for this variant
          editVariant.options.forEach(function(optValue, i) {
            this.selectedOptions[i] = optValue;
            var rows = slot.querySelectorAll('.product-modal__option-row');
            if (rows[i]) {
              var selectedText = rows[i].querySelector('.product-modal__option-selected');
              if (selectedText) selectedText.textContent = optValue;
              rows[i].setAttribute('aria-label', (rows[i].dataset.optionName || 'Option') + '. Currently selected option is ' + optValue + '. Change option');
              var editSource = Array.from(rows[i].querySelectorAll('.product-modal__option-btn')).find(function(optionButton) {
                return optionButton.dataset.optionValue === optValue;
              });
              var editImage = editSource ? editSource.querySelector('.product-modal__option-meta-image') : null;
              var editRowIcon = rows[i].querySelector('.atc-dr-product-variant-icon');
              if (editRowIcon) {
                editRowIcon.replaceChildren();
                if (editImage) {
                  var editRowImage = editImage.cloneNode(true);
                  editRowImage.removeAttribute('class');
                  editRowImage.width = 40;
                  editRowImage.height = 40;
                  editRowIcon.appendChild(editRowImage);
                }
              }
              rows[i].querySelectorAll('.product-modal__option-btn').forEach(function(b) {
                b.classList.toggle('is-selected', b.dataset.optionValue === optValue);
                b.setAttribute('aria-pressed', b.dataset.optionValue === optValue ? 'true' : 'false');
              });
            }
          }.bind(this));

          this.updateVariantUI(editVariant);
        }

        // Pre-select the quantity
        var qtySelect = slot.querySelector('[data-qty-select]');
        if (qtySelect) {
          qtySelect.value = String(this.editMode.quantity);
          // If exact value not in dropdown, find closest
          if (qtySelect.value !== String(this.editMode.quantity)) {
            var bestOption = null;
            for (var i = 0; i < qtySelect.options.length; i++) {
              if (parseInt(qtySelect.options[i].value) >= this.editMode.quantity) {
                bestOption = qtySelect.options[i];
                break;
              }
            }
            if (bestOption) qtySelect.value = bestOption.value;
          }
          this.updateTotal();
        }

        // Change button to "Update Cart"
        var atcBtn = slot.querySelector('[data-action="modal-add-to-cart"]');
        if (atcBtn) {
          atcBtn.textContent = 'Update Cart';
          atcBtn.dataset.action = 'modal-update-cart';
        }
      }
      this.updateCartActionLabel();
      window.requestAnimationFrame(function() {
        if (mainNav) window.StikDialogIsolation?.focusFirst(mainNav);
      });

    } catch (err) {
      console.error('ProductModal.open:', err);
    }
  },

  close(restoreFocus = true) {
    var menuCtx = document.getElementById('js-default-menu');
    var mainNav = document.getElementById('main-nav');
    if (menuCtx) menuCtx.classList.remove('js-menu__expanded');
    if (mainNav) {
      mainNav.classList.remove('js-menu__expanded');
      mainNav.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = '';
    this.closeVariantDrawer();
    window.StikDialogIsolation?.sync();
    const opener = this.opener;
    if (restoreFocus && this.editMode) {
      StikCartDrawer.open(this.editMode.returnCartOpener);
      window.requestAnimationFrame(() => {
        if (opener?.isConnected) opener.focus();
      });
    } else if (restoreFocus && opener && typeof opener.focus === 'function') opener.focus();
    this.opener = null;
  },

  /**
   * Open the variant selection sub-drawer for a given option row
   */
  openVariantDrawer: function(row) {
    var index = parseInt(row.dataset.optionIndex);
    this.variantOpener = row;
    row.setAttribute('aria-expanded', 'true');
    var valuesPanel = row.querySelector('.product-modal__option-values');
    if (!valuesPanel) return;

    var titleEl = document.getElementById('variant_option_title');
    if (titleEl) {
      titleEl.textContent = 'CHOOSE YOUR ' + (row.dataset.optionName || 'OPTION').toUpperCase();
    }

    var buttons = valuesPanel.querySelectorAll('.product-modal__option-btn');
    var currentSelected = this.selectedOptions[index] || '';
    var dataEl = document.getElementById('variant_modal_data');
    if (!dataEl) return;
    dataEl.replaceChildren();

    buttons.forEach(function(btn) {
      var value = btn.dataset.optionValue;
      var isSelected = value === currentSelected;
      var choice = document.createElement('button');
      choice.type = 'button';
      choice.className = 'variant-option-choice variant-option-main' + (isSelected ? ' selected' : '');
      choice.dataset.optionIndex = String(index);
      choice.dataset.optionValue = value;
      choice.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      var imgEl = btn.querySelector('.product-modal__option-meta-image');
      if (imgEl) {
        var icon = document.createElement('span');
        icon.className = 'variant-option-icon';
        icon.appendChild(imgEl.cloneNode(true));
        choice.appendChild(icon);
      } else {
        choice.classList.add('variant-option-main--no-icon');
      }

      var text = document.createElement('span');
      text.className = 'variant-option-text-main';
      var title = document.createElement('span');
      title.className = 'variant-option-text-main1';
      title.textContent = value;
      text.appendChild(title);

      var descriptionSource = btn.querySelector('.product-modal__option-meta-description');
      if (descriptionSource) {
        var description = document.createElement('span');
        description.className = 'variant-option-text-main2';
        description.textContent = descriptionSource.textContent;
        text.appendChild(description);
      }
      choice.appendChild(text);
      dataEl.appendChild(choice);

      var matchCount = parseInt(btn.dataset.optionMatchCount || '0');
      if (matchCount > 1) {
        console.warn('Product option metafield duplicate; using first exact match:', value, matchCount);
      }
    });

    var drawer = document.getElementById('variant_modal');
    if (drawer) {
      drawer.removeAttribute('aria-label');
      drawer.setAttribute('aria-labelledby', 'variant_option_title');
      drawer.classList.add('visible');
      drawer.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(function() {
        window.StikDialogIsolation?.sync();
        var focusTarget = drawer.querySelector('.drawer-close-button, [data-action="close-variant-drawer"], button');
        if (focusTarget) focusTarget.focus();
        else drawer.focus();
      });
    }
  },

  closeVariantDrawer: function() {
    var drawer = document.getElementById('variant_modal');
    if (drawer) {
      drawer.classList.remove('visible');
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (this.variantOpener) {
      this.variantOpener.setAttribute('aria-expanded', 'false');
      window.StikDialogIsolation?.sync();
      this.variantOpener.focus();
      this.variantOpener = null;
    }
  },

  trapFocus(event) {
    const activeDrawer = document.getElementById('variant_modal')?.classList.contains('visible')
      ? document.getElementById('variant_modal')
      : document.getElementById('main-nav');
    if (!activeDrawer) return;
    window.StikDialogIsolation?.moveFocus(event, activeDrawer);
  },

  /**
   * Handle option button selection → find matching variant → update price/availability
   */
  selectOption(btn) {
    const index = parseInt(btn.dataset.optionIndex);
    const value = btn.dataset.optionValue;

    // Update UI
    const group = btn.closest('.product-modal__option-values');
    group?.querySelectorAll('.product-modal__option-btn').forEach((b) =>
      b.classList.remove('is-selected')
    );
    btn.classList.add('is-selected');

    // Track selection
    this.selectedOptions[index] = value;

    // Find matching variant
    const variant = this.findVariant();
    if (variant) {
      this.updateVariantUI(variant);
    }
  },

  /**
   * Find variant matching current selected options
   */
  findVariant() {
    return this.variants.find((v) => {
      return v.options.every(
        (opt, i) => !this.selectedOptions[i] || opt === this.selectedOptions[i]
      );
    });
  },

  /**
   * Update price, availability, and ATC button for selected variant
   */
  updateVariantUI(variant) {
    const modal = document.querySelector('.product-modal');
    if (!modal) return;

    // Update ATC button (may be "modal-add-to-cart" or "modal-update-cart" in edit mode)
    const atcBtn = modal.querySelector('[data-action="modal-add-to-cart"]') || modal.querySelector('[data-action="modal-update-cart"]');
    if (atcBtn) {
      atcBtn.dataset.variantId = variant.id;
      var isEditMode = atcBtn.dataset.action === 'modal-update-cart';
      if (variant.available) {
        atcBtn.disabled = false;
        if (isEditMode) {
          atcBtn.textContent = 'Update Cart';
        } else {
          atcBtn.textContent = 'Add to Cart';
        }
      } else {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Sold Out';
      }
    }

    // Update price display
    const priceEl = modal.querySelector('.product-modal__price');
    if (priceEl) {
      const price = variant.price;
      const compareAt = variant.compare_at_price;
      const unitText = priceEl.querySelector('.price-display__unit')?.textContent || '';
      let html = '';

      if (compareAt && compareAt > price) {
        html += `<span class="price-display"><span class="price-display__compare"><span class="custom-discounted-price-prod-page">${this.formatMoney(compareAt)}</span></span>`;
        if (document.body.dataset.b2b === 'true') {
          html += ` <span class="trade-cart-badge">TRADE</span>`;
        }
        html += ` <span class="price-display__current price-display__current--sale">${this.formatMoney(price)}</span>`;
      } else {
        html = `<span class="price-display"><span class="price-display__current">${this.formatMoney(price)}</span>`;
      }
      if (unitText) html += `<span class="price-display__unit">${unitText}</span>`;
      html += `</span>`;
      priceEl.innerHTML = html;
    }

    this.updateTotal();
    this.updateCartActionLabel();
  },

  /**
   * Update the total price based on variant price × quantity
   */
  updateTotal() {
    const modal = document.querySelector('.product-modal');
    if (!modal) return;

    const variant = this.findVariant();
    if (!variant) return;

    const qty = parseInt(modal.querySelector('[data-qty-select]')?.value || '1');
    const total = variant.price * qty;

    const totalEl = modal.querySelector('[data-total-price]');
    if (totalEl) {
      totalEl.textContent = this.formatMoney(total);
    }

    // Update Affirm amount and refresh — clear innerHTML so Affirm re-renders
    const affirmEl = modal.querySelector('.affirm-as-low-as');
    if (affirmEl) {
      affirmEl.setAttribute('data-amount', total.toString());
      affirmEl.innerHTML = '';
      this.observeAffirmWidget(modal);
      if (typeof affirm !== 'undefined' && affirm.ui) {
        try { affirm.ui.refresh(); } catch (e) {}
      }
    }
  },

  observeAffirmWidget(root) {
    const affirmEl = root ? root.querySelector('.affirm-as-low-as') : null;
    if (!affirmEl) return;
    if (this.affirmObserver) this.affirmObserver.disconnect();
    const applyName = function() {
      const trigger = affirmEl.querySelector('.affirm-modal-trigger, a[href], [role="link"]');
      const accessibleName = affirmEl.dataset.accessibleName;
      if (trigger && accessibleName && trigger.getAttribute('aria-label') !== accessibleName) {
        trigger.setAttribute('aria-label', accessibleName);
      }
    };
    this.affirmObserver = new MutationObserver(applyName);
    this.affirmObserver.observe(affirmEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label'],
    });
    applyName();
  },

  updateCartActionLabel() {
    const modal = document.querySelector('.product-modal');
    if (!modal) return;
    const button = modal.querySelector('[data-action="modal-add-to-cart"], [data-action="modal-update-cart"]');
    const qtySelect = modal.querySelector('[data-qty-select]');
    if (!button || !qtySelect) return;
    const productTitle = modal.dataset.productTitle || button.dataset.productTitle || 'product';
    const options = Array.from(modal.querySelectorAll('.product-modal__option-row')).map(function(row) {
      const selected = row.querySelector('.product-modal__option-selected');
      return (row.dataset.optionName || 'Option') + ': ' + (selected ? selected.textContent.trim() : '');
    }).join(', ');
    const quantity = qtySelect.options[qtySelect.selectedIndex] ? qtySelect.options[qtySelect.selectedIndex].textContent.trim() : qtySelect.value;
    const verb = button.dataset.action === 'modal-update-cart' ? 'Update' : 'Add';
    const suffix = button.dataset.action === 'modal-update-cart' ? ' in cart' : ' to cart';
    button.setAttribute('aria-label', verb + ' ' + productTitle + suffix + '. Selected options: ' + (options || 'default').trim() + '. Quantity: ' + quantity);
  },

  /**
   * Add selected variant + quantity to cart via CartDrawer
   */
  async addToCart() {
    const modal = document.querySelector('.product-modal');
    if (!modal) return;

    const variantId = modal.querySelector('[data-action="modal-add-to-cart"]')?.dataset.variantId;
    const qty = parseInt(modal.querySelector('[data-qty-select]')?.value || '1');

    if (!variantId) return;

    const variant = this.variants.find((v) => String(v.id) === String(variantId));
    const properties = {};

    // Store compare_at_price as hidden property for cart display
    if (variant?.compare_at_price && variant.compare_at_price > variant.price) {
      properties['_compare_at_price'] = (variant.compare_at_price / 100).toFixed(2);
    }

    try {
      const atcBtn = modal.querySelector('[data-action="modal-add-to-cart"]');
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Add to Cart';
      }

      const returnTarget = this.opener;
      await StikCartDrawer.addItem(parseInt(variantId), qty, properties, { open: false });
      this.close(false);
      StikCartDrawer.open(returnTarget);
    } catch (err) {
      console.error('ProductModal.addToCart:', err);
      const atcBtn = modal.querySelector('[data-action="modal-add-to-cart"]');
      if (atcBtn) {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      }
    }
  },

  /**
   * Update an existing cart line item (edit mode)
   * Updates the exact line in place when the variant is unchanged. When the
   * variant changes, establishes the replacement first and then removes only
   * the original line key, with rollback if removal fails.
   */
  async updateCartItem() {
    if (!this.editMode) return;
    const modal = document.querySelector('.product-modal');
    if (!modal) return;

    const atcBtn = modal.querySelector('[data-action="modal-update-cart"]');
    const variantId = atcBtn?.dataset.variantId;
    const qty = parseInt(modal.querySelector('[data-qty-select]')?.value || '1');

    if (!variantId) return;

    const variant = this.variants.find((v) => String(v.id) === String(variantId));
    const properties = Object.assign({}, this.editMode.properties || {});
    if (variant?.compare_at_price && variant.compare_at_price > variant.price) {
      properties['_compare_at_price'] = (variant.compare_at_price / 100).toFixed(2);
    }

    try {
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Update Cart';
      }

      if (String(variantId) === String(this.editMode.variantId)) {
        await StikCartDrawer.updateQuantity(this.editMode.editLineKey, qty);
      } else {
        await StikCartDrawer.replaceItem(
          this.editMode.editLineKey,
          parseInt(variantId),
          qty,
          properties
        );
      }

      const returnCartOpener = this.editMode.returnCartOpener;
      this.close(false);
      StikCartDrawer.open(returnCartOpener);
    } catch (err) {
      console.error('ProductModal.updateCartItem:', err);
      if (atcBtn) {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Update Cart';
      }
    }
  },

  toggleCalculator(btn) {
    var calcContainer = btn.closest('.product-modal__calculator');
    if (!calcContainer) return;

    var wasteFactor = parseFloat(calcContainer.dataset.wasteFactor || '0');
    var sqftPerUnit = parseFloat(calcContainer.dataset.sqftPerUnit || '1');
    var roundUnit = parseInt(calcContainer.dataset.roundUnit || '1');
    var unit = calcContainer.dataset.unit || 'sqft';
    var singularUnit = calcContainer.dataset.unitSingular || 'unit';

    // Set title
    var titleEl = document.getElementById('variant_option_title');
    if (titleEl) titleEl.textContent = 'CALCULATE ' + unit.toUpperCase();

    // Build calculator HTML matching old design
    var html = '';
    html += '<div class="atc-dr-calculate-row">';
    html += '<span class="atc-dr-calculate-row-text">Wall Height, (in Inches) = </span>';
    html += '<input class="get_value atc-dr-calculate-row-input" type="number" aria-label="Wall height in inches" data-calc-height>';
    html += '</div>';

    html += '<div class="atc-dr-calculate-row">';
    html += '<span class="atc-dr-calculate-row-text">Wall Width (in Inches) = </span>';
    html += '<input class="get_value atc-dr-calculate-row-input" type="number" aria-label="Wall width in inches" data-calc-width>';
    html += '</div>';

    html += '<div class="atc-dr-calculate-row">';
    html += '<span class="atc-dr-calculate-row-text">Area of Wall (in Square Feet) = </span>';
    html += '<input class="calculated_value atc-dr-calculate-row-input" id="calculated_value" value="0" disabled>';
    html += '</div>';

    html += '<div class="atc-dr-calculate-row">';
    html += '<span class="atc-dr-calculate-row-text">We Recommend You Order = </span>';
    html += '<button type="button" class="recommended_value atc-dr-calculate-row-btn drawer-action-button" id="recommended_value" data-calc-apply>';
    html += '<span>--</span><span>' + unit.toUpperCase() + '</span></button>';
    html += '</div>';

    html += '<div class="calculatorDrawer__video-container">';
    html += '<video tabindex="0" class="videoSection__video" aria-label="Watch our how-to-measure video tutorial to learn how to measure your walls and determine how much you need to order for your project" title="Watch our how-to-measure video tutorial to learn how to measure your walls and determine how much you need to order for your project" src="https://cdn.shopify.com/videos/c/o/v/81b19e2c754d427fbfa898c7fcab4c0d.mp4" preload="auto" playsinline controls poster="https://cdn.shopify.com/s/files/1/0701/0254/8798/files/measure_poster.webp?v=1675795764"></video>';
    html += '</div>';

    var dataEl = document.getElementById('variant_modal_data');
    if (dataEl) dataEl.innerHTML = html;

    // Open the sub-drawer
    var drawer = document.getElementById('variant_modal');
    if (drawer) {
      this.variantOpener = btn;
      btn.setAttribute('aria-expanded', 'true');
      drawer.classList.add('visible');
      drawer.setAttribute('aria-label', 'Product calculator');
      drawer.removeAttribute('aria-labelledby');
      drawer.setAttribute('aria-hidden', 'false');
      window.StikDialogIsolation?.sync();
      window.requestAnimationFrame(() => window.StikDialogIsolation?.focusFirst(drawer));
    }

    // Bind live calculation
    var self = this;
    var heightInput = dataEl.querySelector('[data-calc-height]');
    var widthInput = dataEl.querySelector('[data-calc-width]');
    var sqftDisplay = dataEl.querySelector('#calculated_value');
    var recommendEl = dataEl.querySelector('#recommended_value');
    var updateRecommendation = function(value) {
      var unitText = Number(value) === 1 ? singularUnit : unit;
      recommendEl.innerHTML = '<span>' + value + '</span><span>' + String(unitText).toUpperCase() + '</span>';
    };

    var updateCalc = function() {
      var h = parseFloat(heightInput.value) || 0;
      var w = parseFloat(widthInput.value) || 0;
      var sqft = (h / 12) * (w / 12);
      sqftDisplay.value = sqft > 0 ? sqft.toFixed(2) : '0';

      if (typeof SqFtCalculator !== 'undefined' && sqft > 0) {
        var qty = SqFtCalculator.calculate(sqft, wasteFactor, sqftPerUnit, roundUnit);
        updateRecommendation(qty);
        recommendEl.dataset.calculatedQty = qty;
      } else {
        updateRecommendation('--');
        recommendEl.dataset.calculatedQty = '0';
      }
    };

    heightInput.addEventListener('input', updateCalc);
    widthInput.addEventListener('input', updateCalc);

    // Apply button — set quantity and close drawer
    recommendEl.addEventListener('click', function(e) {
      e.preventDefault();
      var qty = parseInt(recommendEl.dataset.calculatedQty || '0');
      if (qty <= 0) return;

      var modal = document.querySelector('.product-modal');
      var qtySelect = modal ? modal.querySelector('[data-qty-select]') : null;
      if (qtySelect) {
        var bestOption = null;
        for (var i = 0; i < qtySelect.options.length; i++) {
          if (parseInt(qtySelect.options[i].value) >= qty) {
            bestOption = qtySelect.options[i];
            break;
          }
        }
        if (!bestOption && qtySelect.options.length > 0) {
          bestOption = qtySelect.options[qtySelect.options.length - 1];
        }
        if (bestOption) {
          qtySelect.value = bestOption.value;
          qtySelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      self.closeVariantDrawer();
    });
  },

  /**
   * Format cents to money string (e.g., 1550 → "$15.50")
   */
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  },
};


// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ProductModal.init());
} else {
  ProductModal.init();
}
window.setTimeout(() => ProductModal.init(), 0);

window.ProductModal = ProductModal;

/**
 * Legacy compatibility — maps old setupProductModal(id, ...) onclick calls.
 * Prefers data-product-handle on the button, falls back to handle lookup cache.
 *
 * Call sites should be migrated to:
 *   data-action="open-product-modal" data-product-handle="{{ product.handle }}"
 */
window._productHandleCache = {};

window.setupProductModal = async function (productId) {
  // Try to find handle from the clicked button's data attribute
  const clickedBtn = document.querySelector(`[onclick*="setupProductModal(${productId}"]`);
  let handle = clickedBtn?.dataset.productHandle;

  if (!handle) {
    // Check cache
    handle = window._productHandleCache[productId];
  }

  if (!handle) {
    // Last resort: fetch from Shopify Product JSON API
    try {
      // Shopify storefront doesn't support ID lookup directly,
      // but the products.json endpoint with handle works.
      // We need to search — use the storefront search.
      const resp = await fetch(`/search/suggest.json?q=id:${productId}&resources[type]=product&resources[limit]=1`);
      if (resp.ok) {
        const data = await resp.json();
        const product = data.resources?.results?.products?.[0];
        if (product) {
          handle = product.handle;
          window._productHandleCache[productId] = handle;
        }
      }
    } catch (err) {
      console.error('setupProductModal: could not resolve handle for ID', productId, err);
      return;
    }
  }

  if (handle) {
    ProductModal.open(handle);
  } else {
    console.error('setupProductModal: no handle found for product ID', productId);
  }
};
